"use client";

import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Check, X } from "lucide-react";
import { cropImageToFixed } from "./cropRect";
import { Modal } from "../../../common";
import "./TestimonyThumbCropper.css";

type Props = {
  /** Source image (data URL or URL). */
  imageSrc: string;

  /** Called with a JPEG data URL (468x200 by default). */
  onDone: (dataUrl: string) => void;

  /** Close the modal/cropper. */
  onCancel: () => void;

  /** Optional output size & quality (defaults are safe) */
  outWidth?: number;    // default 468
  outHeight?: number;   // default 200
  quality?: number;     // default 0.92

  /** Optional modal tweaks */
  modalTitle?: string;              // default: "Crop thumbnail (468×200)"
  disableBackdropClick?: boolean;   // default: true
};

const TestimonyThumbCropper: React.FC<Props> = ({
  imageSrc,
  onDone,
  onCancel,
  outWidth = 468,
  outHeight = 200,
  quality = 0.92,
  modalTitle,
  disableBackdropClick = true,
}) => {
  // cropper state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.2);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const aspect = outWidth / outHeight;

  // store pixel area on crop complete
  const onCropComplete = useCallback((_: any, areaPx: any) => {
    setCroppedAreaPixels(areaPx);
  }, []);

  // export fixed-size JPEG
  const handleConfirm = async () => {
    const result = await cropImageToFixed(imageSrc, croppedAreaPixels, {
      width: outWidth,
      height: outHeight,
      format: "image/jpeg",
      quality,
      background: "#000",
    });
    if (result) onDone(result);
  };

  return (
    <Modal
      isOpen={true}                            // always open while mounted
      onClose={onCancel}                       // close on X/backdrop (if allowed)
      title={modalTitle ?? `Crop thumbnail (${outWidth}×${outHeight})`}
      hideFooter                               // use cropper's own buttons
      disableBackdropClick={disableBackdropClick}
      confirmVariant="primary"
    >
      <div className="tthumb-container">
        <div className="tthumb-wrapper">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape="rect"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
          <div className="tthumb-dim-badge">{outWidth} × {outHeight}</div>
        </div>

        <div className="tthumb-controls">
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(+e.target.value)}
            aria-label="Zoom"
          />
        </div>

        <div className="tthumb-actions">
          <button
            type="button"
            className="tthumb-btn cancel"
            onClick={onCancel}
            title="Cancel"
          >
            <X size={24} strokeWidth={4} />
          </button>
          <button
            type="button"
            className="tthumb-btn confirm"
            onClick={handleConfirm}
            title="Crop"
          >
            <Check size={24} strokeWidth={4} />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TestimonyThumbCropper;
